import React from "react";
import { TiCardActor, TiCardQueryResponse } from "../../../../@types";
import { RenderWhen } from "../../../../../../components";
import TiCardActors from "./TiCardActors";

interface Props {
  data: TiCardQueryResponse;
  onActorClick?: (actor: TiCardActor) => void;
  onOpenThreatNameDetails?: () => void;
  hideActors?: boolean;
}

interface RowProps {
  condition: boolean;
  title: React.ReactNode;
  children: React.ReactNode;
  "data-testid"?: string;
}

const RenderRow = ({
  condition,
  title,
  children,
  "data-testid": dataTestId
}: RowProps) => (
  <RenderWhen condition={condition}>
    <div className={"ticard-content__information-row"} data-testid={dataTestId}>
      <span
        className={
          "ticard-content__information-row__title pdf-ticard-info-item"
        }
      >
        {title}
      </span>
      {children}
    </div>
  </RenderWhen>
);

const TiCardGeneralInfo = ({
  data,
  onActorClick,
  onOpenThreatNameDetails,
  hideActors = false
}: Props) => {
  const activeFamilyData = data.rl.general;

  const targetedOs = activeFamilyData.targeted_os?.join(", ");
  const targetedIndustries = activeFamilyData.targeted_industries?.join(", ");
  const threatNameAlias = activeFamilyData.alias?.join(", ");

  return (
    <div className={"ticard-content__information-rows"}>
      <RenderRow
        condition={!!activeFamilyData.threat_name}
        title={"Threat name: "}
        data-testid="ticard_information_row_threat_name"
      >
        <p
          className={"ticard-content__information-row__link"}
          onClick={onOpenThreatNameDetails}
          data-testid="ticard_information_row_threat_name_link"
        >
          {activeFamilyData.threat_name}
        </p>
      </RenderRow>
      <RenderRow
        condition={!!threatNameAlias}
        title={`Threat name alias (${activeFamilyData.alias?.length}): `}
        data-testid="ticard_information_threat_name_alias"
      >
        {threatNameAlias}
      </RenderRow>
      <RenderRow
        condition={!!activeFamilyData.first_seen}
        title={"First seen: "}
        data-testid="ticard_information_row_first_seen"
      >
        {activeFamilyData.first_seen}
      </RenderRow>
      <RenderRow
        condition={!!activeFamilyData.last_seen}
        title={"Last seen: "}
        data-testid="ticard_information_row_last_seen"
      >
        {activeFamilyData.last_seen}
      </RenderRow>
      <RenderRow
        condition={!!activeFamilyData.latest_version}
        title={"Current latest version: "}
        data-testid="ticard_information_row_current_latest_version"
      >
        {activeFamilyData.latest_version}
      </RenderRow>
      <RenderRow
        condition={!!activeFamilyData.malware_type}
        title={"Malware family type: "}
        data-testid="ticard_information_row_malware_family_type"
      >
        {activeFamilyData.malware_type}
      </RenderRow>
      <RenderRow
        condition={!!activeFamilyData.packer}
        title={"Packer: "}
        data-testid="ticard_information_row_packer"
      >
        {activeFamilyData.packer}
      </RenderRow>
      <RenderRow
        condition={!!targetedOs}
        title={"Targeted OS: "}
        data-testid="ticard_information_row_targeted_os"
      >
        {targetedOs}
      </RenderRow>
      <RenderRow
        condition={!!targetedIndustries}
        title={"Targeted industries: "}
        data-testid="ticard_information_row_targeted_industries"
      >
        {targetedIndustries}
      </RenderRow>
      {!hideActors && (
        <RenderRow
          condition={!!activeFamilyData.actor?.length}
          title={`Actor aliases (${activeFamilyData.actor?.length}): `}
          data-testid="ticard_information_row_actor_aliases"
        >
          <TiCardActors
            actors={data.rl.general.actor || []}
            onActorClick={onActorClick}
          />
        </RenderRow>
      )}
    </div>
  );
};

export default TiCardGeneralInfo;
